import React, { useEffect, useState } from "react";
import { Row, Col, Input, Form, Container, Spinner, Label, FormFeedback } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { debounce } from "lodash";
import Footer from "components/VerticalLayout/Footer";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { put } from "helpers/api_helper";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import {
  getAllCountries,
  getCitiesForState,
  getStatesForCountry,
} from "helpers/utils/locationUtil";
import { zipcode } from "helpers/validations/regex";
import secureLocalStorage from "react-secure-storage";
import * as api from "../../constants/api_constants";

function AddVendorShippingBilling({
  entityType = "vendor",
  navigatePath = "/vendors",
  sameAsLabel = "Vendor Address",
}) {
  const { vendorId } = useParams();
  const [isAddressSame, setIsAddressSame] = useState({
    sameAsEntity: false,
    sameAsBilling: false,
  });
  const [billingLocations, setBillingLocations] = useState({
    states: [],
    cities: [],
  });
  const [shippingLocations, setShippingLocations] = useState({
    states: [],
    cities: [],
  });
  const [countries, setCountries] = useState([]);
  const [entityData, setEntityData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state;
  
  console.log("navState",navState);
  

  const storedUser = secureLocalStorage.getItem("authUser");
  const user = storedUser ? JSON.parse(storedUser) : null;

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        // Fetch countries
        const fetchedCountries = getAllCountries();
        if (!fetchedCountries?.length) {
          showErrorToast("No countries available");
          navigate("/add-vendor");
          return;
        }
        setCountries(fetchedCountries);

        if (!navState?.vendorId || !navState?.vendorData) {
          showErrorToast(
            "No vendor data found. Please fill vendor details first."
          );
          navigate("/add-vendor");
          return;
        }

        const vendorData = navState.vendorData;
        const entity = {
          company: vendorData.companyName || vendorData.company || "",
          vatNumber: vendorData.vat || vendorData.vatNumber || "",
          website: vendorData.website || "",
          phone: vendorData.phone || "",
          primaryContactName: vendorData.primaryContactName || "",
          primaryContactEmail: vendorData.primaryContactEmail || "",
          primaryContactPhone: vendorData.primaryContactPhone || "",
          primaryContactDesignation: vendorData.primaryContactDesignation || "",
          currency: vendorData.currency || "",
          country: vendorData.country || "",
          province: vendorData.province || "",
          zipCode: vendorData.zipcode || vendorData.zipCode || "",
          city: vendorData.city || "",
          address: vendorData.address || "",
          paymentTerms: vendorData.paymentTerms || "",
        };

        setEntityData(entity);

        if (isAddressSame.sameAsEntity && entity.country) {
          validation.setFieldValue("billingCountry", entity.country);
          validation.setFieldValue("billingZipCode", entity.zipCode);
          validation.setFieldValue("billingProvince", entity.province);
          validation.setFieldValue("billingCity", entity.city);
          validation.setFieldValue("billingStreet", entity.address);

          const states = getStatesForCountry(entity.country);
          setBillingLocations({ states, cities: [] });
          if (entity.province) {
            const cities = getCitiesForState(entity.country, entity.province);
            setBillingLocations((prev) => ({ ...prev, cities }));
          }
        }

        if (vendorId && entity.shippingAddress?.country) {
          const shippingStates = getStatesForCountry(entity.shippingAddress.country);
          setShippingLocations((prev) => ({ ...prev, states: shippingStates }));
          if (entity.shippingAddress.province) {
            const shippingCities = getCitiesForState(
              entity.shippingAddress.country,
              entity.shippingAddress.province
            );
            setShippingLocations((prev) => ({ ...prev, cities: shippingCities }));
          }
        }

        // Initialize form values
        validation.setValues({
          billingCountry: vendorId ? (entity.billingAddress?.country || "") : "",
          billingZipCode: vendorId ? (entity.billingAddress?.zipCode || "") : "",
          billingProvince: vendorId ? (entity.billingAddress?.province || "") : "",
          billingCity: vendorId ? (entity.billingAddress?.city || "") : "",
          billingStreet: vendorId ? (entity.billingAddress?.street || "") : "",
          shippingCountry: entity.shippingAddress?.country || "",
          shippingZipCode: entity.shippingAddress?.zipCode || "",
          shippingProvince: entity.shippingAddress?.province || "",
          shippingCity: entity.shippingAddress?.city || "",
          shippingStreet: entity.shippingAddress?.street || "",
        });
      } catch (error) {
        showErrorToast(`Failed to initialize data: ${error.message}`);
        navigate("/add-vendor");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
  }, [navigate, isAddressSame.sameAsEntity]);

  const debouncedFetchStates = debounce((country, callback) => {
    const states = getStatesForCountry(country);
    if (!states.length && country) {
      console.warn(`No states found for country: ${country}`);
      showErrorToast(`No states available for country: ${country}`);
    }
    callback(states);
  }, 300);

  const debouncedFetchCities = debounce((country, state, callback) => {
    const cities = getCitiesForState(country, state);
    if (!cities.length && country && state) {
      console.warn(`No cities found for country: ${country}, state: ${state}`);
      showErrorToast(`No cities available for state: ${state}`);
    }
    callback(cities);
  }, 300);

  const handleBillingCountryChange = (e) => {
    const selectedCountry = e.target.value;
    validation.setFieldValue("billingCountry", selectedCountry);

    debouncedFetchStates(selectedCountry, (states) => {
      setBillingLocations({ states, cities: [] });
      validation.setFieldValue("billingProvince", "");
      validation.setFieldValue("billingCity", "");

      if (isAddressSame.sameAsBilling) {
        validation.setFieldValue("shippingCountry", selectedCountry);
        setShippingLocations({ states, cities: [] });
        validation.setFieldValue("shippingProvince", "");
        validation.setFieldValue("shippingCity", "");
      }
    });
  };

  const handleBillingStateChange = (e) => {
    const selectedState = e.target.value;
    validation.setFieldValue("billingProvince", selectedState);

    debouncedFetchCities(
      validation.values.billingCountry,
      selectedState,
      (cities) => {
        setBillingLocations((prev) => ({ ...prev, cities }));
        validation.setFieldValue("billingCity", "");

        if (isAddressSame.sameAsBilling) {
          validation.setFieldValue("shippingProvince", selectedState);
          setShippingLocations((prev) => ({ ...prev, cities }));
          validation.setFieldValue("shippingCity", "");
        }
      }
    );
  };

  const handleShippingCountryChange = (e) => {
    const selectedCountry = e.target.value;
    validation.setFieldValue("shippingCountry", selectedCountry);

    debouncedFetchStates(selectedCountry, (states) => {
      setShippingLocations({ states, cities: [] });
      validation.setFieldValue("shippingProvince", "");
      validation.setFieldValue("shippingCity", "");
    });
  };

  const handleShippingStateChange = (e) => {
    const selectedState = e.target.value;
    validation.setFieldValue("shippingProvince", selectedState);

    debouncedFetchCities(
      validation.values.shippingCountry,
      selectedState,
      (cities) => {
        setShippingLocations((prev) => ({ ...prev, cities }));
        validation.setFieldValue("shippingCity", "");
      }
    );
  };

  const handleSameAsEntity = (e) => {
    const isChecked = e.target.checked;
    setIsAddressSame((prev) => ({ ...prev, sameAsEntity: isChecked }));

    console.log("Checkbox Toggled:", isChecked);
    console.log("Entity Data for Checkbox:", entityData);

    if (isChecked && entityData) {
      const country = entityData.country || "";
      const province = entityData.province || "";
      const city = entityData.city || "";
      const zipCode = entityData.zipCode || "";
      const street = entityData.address || "";

      // Validate country
      if (country && !countries.some((c) => c.value === country)) {
        console.warn(`Invalid country code: ${country}`);
        showErrorToast(`Invalid country in vendor data: ${country}`);
        return;
      }

      // Validate province and city
      if (country && province) {
        const states = getStatesForCountry(country);
        if (!states.some((s) => s.value === province)) {
          console.warn(`Invalid province: ${province} for country: ${country}`);
          showErrorToast(`Invalid province in vendor data: ${province}`);
          return;
        }
        if (city) {
          const cities = getCitiesForState(country, province);
          if (!cities.some((c) => c.value === city)) {
            console.warn(`Invalid city: ${city} for province: ${province}`);
            showErrorToast(`Invalid city in vendor data: ${city}`);
            return;
          }
        }
      }

      // Set form values
      validation.setFieldValue("billingCountry", country);
      validation.setFieldValue("billingZipCode", zipCode);
      validation.setFieldValue("billingProvince", province);
      validation.setFieldValue("billingCity", city);
      validation.setFieldValue("billingStreet", street);

      console.log("Setting Billing Fields:", {
        billingCountry: country,
        billingZipCode: zipCode,
        billingProvince: province,
        billingCity: city,
        billingStreet: street,
      });

      if (country) {
        const states = getStatesForCountry(country);
        setBillingLocations((prev) => ({ ...prev, states }));
        console.log("Fetched States:", states);

        if (province) {
          const cities = getCitiesForState(country, province);
          setBillingLocations((prev) => ({ ...prev, cities }));
          console.log("Fetched Cities:", cities);
        } else {
          setBillingLocations((prev) => ({ ...prev, cities: [] }));
        }
      } else {
        setBillingLocations({ states: [], cities: [] });
      }

      setTimeout(() => {
        console.log("Form Values After Checkbox:", validation.values);
      }, 0);
    } else {
      validation.setFieldValue("billingCountry", "");
      validation.setFieldValue("billingZipCode", "");
      validation.setFieldValue("billingProvince", "");
      validation.setFieldValue("billingCity", "");
      validation.setFieldValue("billingStreet", "");
      setBillingLocations({ states: [], cities: [] });
      console.log("Cleared Billing Fields");
    }
  };

  const handleSameAsBilling = (e) => {
    const isChecked = e.target.checked;
    setIsAddressSame((prev) => ({ ...prev, sameAsBilling: isChecked }));

    if (isChecked) {
      validation.setFieldValue("shippingCountry", validation.values.billingCountry);
      validation.setFieldValue("shippingZipCode", validation.values.billingZipCode);
      validation.setFieldValue("shippingProvince", validation.values.billingProvince);
      validation.setFieldValue("shippingCity", validation.values.billingCity);
      validation.setFieldValue("shippingStreet", validation.values.billingStreet);

      setShippingLocations({
        states: billingLocations.states,
        cities: billingLocations.cities,
      });
    } else {
      validation.setFieldValue("shippingCountry", "");
      validation.setFieldValue("shippingZipCode", "");
      validation.setFieldValue("shippingProvince", "");
      validation.setFieldValue("shippingCity", "");
      validation.setFieldValue("shippingStreet", "");
      setShippingLocations({ states: [], cities: [] });
    }
  };

  const zipCodeValidation = (country) => {
    if (country === "US") {
      return Yup.string().matches(
        /^\d{5}(-\d{4})?$/,
        "Invalid US zip code (e.g., 12345 or 12345-6789)"
      );
    }
    return Yup.string().matches(
      zipcode,
      "Zip code must be 4-8 characters long and can include only numbers"
    );
  };

  const initialFormValues = {
    billingCountry: "",
    billingZipCode: "",
    billingProvince: "",
    billingCity: "",
    billingStreet: "",
    shippingCountry: "",
    shippingZipCode: "",
    shippingProvince: "",
    shippingCity: "",
    shippingStreet: "",
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: initialFormValues,
    validationSchema: Yup.object({
      billingCountry: Yup.string().required("Billing country is required"),
      billingZipCode: Yup.string()
        .required("Billing zip code is required")
        .test("billingZipCode", "Invalid zip code", function (value) {
          return zipCodeValidation(this.parent.billingCountry).isValidSync(
            value
          );
        }),
      billingProvince: Yup.string().required("Billing state is required"),
      billingCity: Yup.string().required("Billing city is required"),
      billingStreet: Yup.string(),
      shippingCountry: Yup.string().required("Shipping country is required"),
      shippingZipCode: Yup.string()
        .required("Shipping zip code is required")
        .test("shippingZipCode", "Invalid zip code", function (value) {
          return zipCodeValidation(this.parent.shippingCountry).isValidSync(
            value
          );
        }),
      shippingProvince: Yup.string().required("Shipping state is required"),
      shippingCity: Yup.string().required("Shipping city is required"),
      shippingStreet: Yup.string(),
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      try {
        if (!navState?.vendorId) {
          throw new Error("Vendor ID not found");
        }

        const vendorData = navState.vendorData;
        const updatedDate = new Date().toISOString();
        const vendorPayload = {
          vendorDto: {
            subscriptionId: 100,
            companyName: vendorData.companyName || vendorData.company,
            vat: vendorData.vat || vendorData.vatNumber,
            currency: vendorData.currency,
            website: vendorData.website || undefined,
            designation: vendorData.primaryContactDesignation || undefined,
            status: 1,
            primaryPhone: vendorData.primaryContactPhone,
            primaryContactName: vendorData.primaryContactName,
            province: vendorData.province,
            zipCode: vendorData.zipcode || vendorData.zipCode,
            city: vendorData.city,
            address: vendorData.address || undefined,
            country: vendorData.country,
            email: vendorData.primaryContactEmail,
            phone: vendorData.phone || undefined,
            paymentTerms: Number(vendorData.paymentTerms),
            createdBy: user?.email || "unknown",
            updatedBy: user?.email || "unknown",
            createdDate: vendorData.createdDate || updatedDate,
            updatedDate: updatedDate,
            billingAddress: {
              street: values.billingStreet || undefined,
              city: values.billingCity,
              province: values.billingProvince,
              zipCode: values.billingZipCode,
              country: values.billingCountry,
            },
            shippingAddress: {
              street: values.shippingStreet || undefined,
              city: values.shippingCity,
              province: values.shippingProvince,
              zipCode: values.shippingZipCode,
              country: values.shippingCountry,
            },
          },
        };
        debugger

        console.log(
          "Sending payload to UPDATE_VENDOR_DATA_BY_ID:",
          JSON.stringify(vendorPayload, null, 2)
        );
        const response = await put(
          api.UPDATE_VENDOR_DATA_BY_ID(navState.vendorId),
          vendorPayload
        );
        console.log(
          "API Response:",
          JSON.stringify(response, null, 2)
        );

        if (response?.status === "/Success") {
          showSuccessToast("Vendor billing and shipping addresses updated successfully");
          secureLocalStorage.removeItem("pendingVendorData_new");
          navigate(navigatePath);
          resetForm({ values: initialFormValues });
          setIsAddressSame({ sameAsEntity: false, sameAsBilling: false });
          setBillingLocations({ states: [], cities: [] });
          setShippingLocations({ states: [], cities: [] });
          navigate("/vendors")
        } else {
          throw new Error(
            response?.data?.message || "Failed to update vendor addresses"
          );
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.message || `Failed to update vendor addresses: ${error.message}`;
        console.error("API Error:", errorMessage, error);
        showErrorToast(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="page-content">
      <Container fluid style={{ padding: "0px", gap: "10px" }}>
        <PageSubHeader
          heading={`Add Billing and Shipping for ${entityType}`}
          routePath="Home"
          pageTitle="Add Billing/Shipping"
        />
        {isLoading ? (
          <div className="text-center my-3">
            <Spinner color="primary" />
          </div>
        ) : (
          <Form>
            <Row className="col-12 justify-content-around my-3 px-5">
              <Col
                md={6}
                className="col-5 px-0 text-nowrap d-flex justify-content-start gap-1"
              >
                <Input
                  type="checkbox"
                  id="sameAsEntity"
                  name="sameAsEntity"
                  onChange={handleSameAsEntity}
                  onBlur={validation.handleBlur}
                  checked={isAddressSame.sameAsEntity}
                  style={{ cursor: "pointer" }}
                  aria-label={`Billing address is the same as the ${sameAsLabel}`}
                />
                <Label htmlFor="sameAsEntity" className="m-1">
                  Billing address is the same as the {sameAsLabel}
                </Label>
              </Col>
              <Col md={6} className="d-flex justify-content-start gap-1 ps-5">
                <Input
                  type="checkbox"
                  id="sameAsBilling"
                  name="SameAsBilling"
                  onChange={handleSameAsBilling}
                  onBlur={validation.handleBlur}
                  checked={isAddressSame.sameAsBilling}
                  style={{ cursor: "pointer" }}
                  aria-label="Shipping address is the same as the billing address"
                />
                <Label htmlFor="sameAsBilling" className="m-1">
                  Shipping address is the same as the billing address
                </Label>
              </Col>
              <Col className="col-5"></Col>
            </Row>
            <Row className="mb-3 text-nowrap"></Row>
            <Row className="justify-content-around">
              <Col md={5}>
                <h5 className="text-primary">Billing Address</h5>
                <Row className="mb-3">
                  <Label htmlFor="billingCountry" className="col-form-label">
                    Country <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="billingCountry"
                    name="billingCountry"
                    type="select"
                    className="form-control"
                    onChange={handleBillingCountryChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.billingCountry || ""}
                    invalid={
                      validation.touched.billingCountry &&
                      !!validation.errors.billingCountry
                    }
                    aria-required="true"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.billingCountry &&
                    validation.errors.billingCountry && (
                      <FormFeedback>
                        {validation.errors.billingCountry}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="billingProvince" className="col-form-label">
                    Province <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="billingProvince"
                    name="billingProvince"
                    type="select"
                    className="form-control"
                    onChange={handleBillingStateChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.billingProvince || ""}
                    invalid={
                      validation.touched.billingProvince &&
                      !!validation.errors.billingProvince
                    }
                    disabled={!billingLocations.states.length}
                    aria-required="true"
                  >
                    <option value="">Select Province</option>
                    {billingLocations.states.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.billingProvince &&
                    validation.errors.billingProvince && (
                      <FormFeedback>
                        {validation.errors.billingProvince}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="billingCity" className="col-form-label">
                    City <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="billingCity"
                    name="billingCity"
                    type="select"
                    className="form-control"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.billingCity || ""}
                    invalid={
                      validation.touched.billingCity &&
                      !!validation.errors.billingCity
                    }
                    disabled={!billingLocations.cities.length}
                    aria-required="true"
                  >
                    <option value="">Select City</option>
                    {billingLocations.cities.map((city) => (
                      <option key={city.value} value={city.value}>
                        {city.label}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.billingCity &&
                    validation.errors.billingCity && (
                      <FormFeedback>
                        {validation.errors.billingCity}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="billingZipCode" className="col-form-label">
                    Zip Code <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="billingZipCode"
                    name="billingZipCode"
                    type="text"
                    className="form-control"
                    placeholder="Enter zip code"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.billingZipCode || ""}
                    invalid={
                      validation.touched.billingZipCode &&
                      !!validation.errors.billingZipCode
                    }
                    aria-required="true"
                  />
                  {validation.touched.billingZipCode &&
                    validation.errors.billingZipCode && (
                      <FormFeedback>
                        {validation.errors.billingZipCode}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="billingStreet" className="col-form-label">
                    Address
                  </Label>
                  <Input
                    type="textarea"
                    id="billingStreet"
                    name="billingStreet"
                    maxLength="225"
                    rows="2"
                    className="form-control"
                    placeholder="Enter your address"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.billingStreet || ""}
                    invalid={
                      validation.touched.billingStreet &&
                      !!validation.errors.billingStreet
                    }
                  />
                  {validation.touched.billingStreet &&
                    validation.errors.billingStreet && (
                      <FormFeedback>
                        {validation.errors.billingStreet}
                      </FormFeedback>
                    )}
                </Row>
              </Col>
              <Col md={5}>
                <h5 className="text-primary">Shipping Address</h5>
                <Row className="mb-3">
                  <Label htmlFor="shippingCountry" className="col-form-label">
                    Country <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="shippingCountry"
                    name="shippingCountry"
                    type="select"
                    className="form-control"
                    onChange={handleShippingCountryChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.shippingCountry || ""}
                    invalid={
                      validation.touched.shippingCountry &&
                      !!validation.errors.shippingCountry
                    }
                    aria-required="true"
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.value} value={country.value}>
                        {country.label}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.shippingCountry &&
                    validation.errors.shippingCountry && (
                      <FormFeedback>
                        {validation.errors.shippingCountry}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="shippingProvince" className="col-form-label">
                    Province <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="shippingProvince"
                    name="shippingProvince"
                    type="select"
                    className="form-control"
                    onChange={handleShippingStateChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.shippingProvince || ""}
                    invalid={
                      validation.touched.shippingProvince &&
                      !!validation.errors.shippingProvince
                    }
                    disabled={!shippingLocations.states.length}
                    aria-required="true"
                  >
                    <option value="">Select Province</option>
                    {shippingLocations.states.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.shippingProvince &&
                    validation.errors.shippingProvince && (
                      <FormFeedback>
                        {validation.errors.shippingProvince}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="shippingCity" className="col-form-label">
                    City <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="shippingCity"
                    name="shippingCity"
                    type="select"
                    className="form-control"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.shippingCity || ""}
                    invalid={
                      validation.touched.shippingCity &&
                      !!validation.errors.shippingCity
                    }
                    disabled={!shippingLocations.cities.length}
                    aria-required="true"
                  >
                    <option value="">Select City</option>
                    {shippingLocations.cities.map((city) => (
                      <option key={city.value} value={city.value}>
                        {city.label}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.shippingCity &&
                    validation.errors.shippingCity && (
                      <FormFeedback>
                        {validation.errors.shippingCity}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="shippingZipCode" className="col-form-label">
                    Zip Code <span className="text-danger">*</span>
                    <span className="visually-hidden">Required</span>
                  </Label>
                  <Input
                    id="shippingZipCode"
                    name="shippingZipCode"
                    type="text"
                    className="form-control"
                    placeholder="Enter zip code"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.shippingZipCode || ""}
                    invalid={
                      validation.touched.shippingZipCode &&
                      !!validation.errors.shippingZipCode
                    }
                    aria-required="true"
                  />
                  {validation.touched.shippingZipCode &&
                    validation.errors.shippingZipCode && (
                      <FormFeedback>
                        {validation.errors.shippingZipCode}
                      </FormFeedback>
                    )}
                </Row>
                <Row className="mb-3">
                  <Label htmlFor="shippingStreet" className="col-form-label">
                    Address
                  </Label>
                  <Input
                    type="textarea"
                    id="shippingStreet"
                    name="shippingStreet"
                    maxLength="225"
                    rows="2"
                    className="form-control"
                    placeholder="Enter your address"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.shippingStreet || ""}
                    invalid={
                      validation.touched.shippingStreet &&
                      !!validation.errors.shippingStreet
                    }
                  />
                  {validation.touched.shippingStreet &&
                    validation.errors.shippingStreet && (
                      <FormFeedback>
                        {validation.errors.shippingStreet}
                      </FormFeedback>
                    )}
                </Row>
              </Col>
            </Row>
            <Footer
              save={validation.handleSubmit}
              cancel={() => {
                secureLocalStorage.removeItem("pendingVendorData_new");
                navigate(navigatePath);
              }}
              disabled={isLoading}
            />
          </Form>
        )}
        {/* <Footer
          save={validation.handleSubmit}
          cancel={() => {
            secureLocalStorage.removeItem("pendingVendorData_new");
            navigate(navigatePath);
          }}
          disabled={isLoading}
        /> */}
      </Container>
    </div>
  );
}

export default AddVendorShippingBilling;